import { CheckCircle2 } from "lucide-react";

interface Props {
  count: number;
  threshold: number | null | undefined;
  /** compact = smaller variant for admin lists */
  compact?: boolean;
}

/**
 * Reusable "Potential Future Sessions" interest meter.
 *
 * Renders a progress bar toward the configured interest_threshold, or a plain
 * count when no threshold is set. Shows a positive "Goal Reached" state when
 * count >= threshold. Purely presentational — no data fetching, no side effects.
 */
const FutureSessionMeter = ({ count, threshold, compact }: Props) => {
  const hasThreshold = typeof threshold === "number" && threshold > 0;
  const reached = hasThreshold && count >= (threshold as number);
  const pct = hasThreshold
    ? Math.min(100, Math.round((count / (threshold as number)) * 100))
    : 0;
  const familyWord = count === 1 ? "family" : "families";

  return (
    <div className={compact ? "space-y-1" : "space-y-1.5"}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className={reached ? "font-semibold text-emerald-800" : "text-foreground"}>
          {hasThreshold
            ? `${count} of ${threshold} ${familyWord} interested`
            : `${count} ${familyWord} interested`}
        </span>
        {hasThreshold && (
          <span className="text-muted-foreground tabular-nums">{pct}%</span>
        )}
      </div>
      {hasThreshold && (
        <div
          className={`w-full ${compact ? "h-1.5" : "h-2"} rounded-full bg-muted overflow-hidden`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <div
            className={`h-full transition-all ${
              reached ? "bg-emerald-500" : "bg-primary/70"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {reached && !compact && (
        <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900">
            <p className="font-semibold">Interest Goal Reached</p>
            <p>
              Enough families have expressed interest for us to consider opening this
              session. Final scheduling and instructor confirmation are still pending.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FutureSessionMeter;
